import { api } from '../utils/api';

export interface AutoscalingStatus {
  enabled: boolean;
  currentWorkers: number;
  currentConcurrency: number;
  queueDepth: number;
  maxWorkers: number;
  maxConcurrency: number;
  lastDecision?: {
    action: 'scale_up' | 'scale_down' | 'maintain';
    targetWorkerCount: number;
    targetConcurrency: number;
    reason: string;
    confidence: number;
    timestamp: string;
  };
}

export interface AutoscalingMetrics {
  queueDepth: number;
  processingRate: number;
  avgProcessingTime: number;
  errorRate: number;
  timestamp: string;
}

export interface AutoscalingDecision {
  action: 'scale_up' | 'scale_down' | 'maintain';
  targetWorkerCount: number;
  targetConcurrency: number;
  reason: string;
  confidence: number;
}

export interface AutoscalingResult {
  success: boolean;
  decision?: AutoscalingDecision;
  newWorkerCount?: number;
  metrics?: AutoscalingMetrics;
  error?: string;
  skipped?: boolean;
  reason?: string;
}

class AutoscalingService {
  /**
   * Get current autoscaling status and metrics
   */
  async getStatus(): Promise<AutoscalingStatus> {
    try {
      const response = await api.get('/v1/system/autoscaling');
      return response;
    } catch (error) {
      console.error('Error getting autoscaling status:', error);
      throw error;
    }
  }

  /**
   * Enable or disable autoscaling
   */
  async setEnabled(enabled: boolean): Promise<{ 
    autoscalingEnabled: boolean; 
    message: string; 
    timestamp: string; 
  }> {
    try {
      const response = await api.post('/v1/system/autoscaling', {
        enabled
      });
      return response;
    } catch (error) {
      console.error('Error updating autoscaling setting:', error);
      throw error;
    }
  }

  /**
   * Manually trigger autoscaling (for testing/immediate action)
   */
  async triggerManual(): Promise<{
    message: string;
    result: AutoscalingResult;
    timestamp: string;
  }> {
    try {
      const response = await api.put('/v1/system/autoscaling/trigger');
      return response;
    } catch (error) {
      console.error('Error triggering manual autoscaling:', error);
      throw error;
    }
  }

  /**
   * Get historical autoscaling decisions
   */
  async getDecisionHistory(limit: number = 10): Promise<Array<{
    decision: AutoscalingDecision;
    metrics: AutoscalingMetrics;
    timestamp: string;
  }>> {
    try {
      // This would need a new backend endpoint to get AI decisions history
      // For now, we'll use the metrics endpoint and extract what we can
      const response = await api.get('/v1/metrics');
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to get decision history');
      }
      
      // Extract autoscaling-related metrics if available
      return [];
    } catch (error) {
      console.error('Error getting autoscaling decision history:', error);
      return [];
    }
  }

  /**
   * Real-time monitoring hook data
   */
  async getMonitoringData(): Promise<{
    enabled: boolean;
    status: 'healthy' | 'scaling' | 'error' | 'disabled';
    workers: {
      active: number;
      target: number;
      capacity: number;
      max: number;
    };
    concurrency: {
      current: number;
      max: number;
      utilization: number;
    };
    queue: {
      depth: number;
      processingRate: number;
      avgTime: number;
    };
    lastAction?: {
      action: string;
      timestamp: string;
      reason: string;
    };
  }> {
    try {
      const status = await this.getStatus();
      
      // Calculate derived metrics
      const utilization = status.currentConcurrency > 0 
        ? Math.min(100, (status.currentWorkers * 20) / status.currentConcurrency * 100)
        : 0;
      
      let systemStatus: 'healthy' | 'scaling' | 'error' | 'disabled' = 'healthy';
      
      if (!status.enabled) {
        systemStatus = 'disabled';
      } else if (status.queueDepth > 200) {
        systemStatus = 'scaling';
      } else if (status.lastDecision?.action !== 'maintain') {
        systemStatus = 'scaling';
      }
      
      return {
        enabled: status.enabled,
        status: systemStatus,
        workers: {
          active: status.currentWorkers,
          target: status.lastDecision?.targetWorkerCount || status.currentWorkers,
          capacity: status.currentWorkers * 20, // Assuming 20 capacity per worker
          max: status.maxWorkers
        },
        concurrency: {
          current: status.currentConcurrency,
          max: status.maxConcurrency,
          utilization
        },
        queue: {
          depth: status.queueDepth,
          processingRate: 0, // Would need additional endpoint
          avgTime: 0 // Would need additional endpoint
        },
        lastAction: status.lastDecision ? {
          action: status.lastDecision.action,
          timestamp: status.lastDecision.timestamp,
          reason: status.lastDecision.reason
        } : undefined
      };
    } catch (error) {
      console.error('Error getting monitoring data:', error);
      throw error;
    }
  }
}

export const autoscalingService = new AutoscalingService();